import React, { useState } from 'react';
import { api, isSpendApprovalError } from './api';
import { toast } from '../hooks/use-toast';
import { errorMessage } from './errors';
import { Eye, Loader2, Send } from 'lucide-react';

// Preview and test send for an email draft. Preview is the email as one recipient gets it (merge
// fields filled from your own name, footer and unsubscribe link added), shown in a sandboxed frame
// so the sender's HTML cannot run. Test send goes to your own address only.
export default function DraftActions({ subject, html, subjectNote = 'Uses Subject Line A.' }) {
  const [busy, setBusy] = useState('');
  const [preview, setPreview] = useState(null);
  const ready = subject.trim() && html.trim();
  const draft = { subject: subject.trim(), html };

  const showPreview = async () => {
    setBusy('preview');
    try {
      const { data } = await api.post('/email/preview', draft);
      setPreview(data);
    } catch (e) {
      toast({ title: 'Preview failed', description: errorMessage(e, 'Could not build the preview') });
    } finally { setBusy(''); }
  };

  const sendTest = async () => {
    setBusy('test');
    try {
      const { data } = await api.post('/email/test-send', draft);
      if (data.sent) toast({ title: 'Test sent', description: `Check ${data.to}. Links in it are live.` });
      else toast({ title: 'Test not sent', description: data.blocked ? `${data.to} is opted out or on the suppression list.` : (data.last_error || 'The email provider did not accept it.') });
    } catch (e) {
      if (isSpendApprovalError(e)) return;
      toast({ title: 'Test send failed', description: errorMessage(e, 'Could not send the test') });
    } finally { setBusy(''); }
  };

  const btn = 'inline-flex items-center gap-1 text-[11px] font-semibold px-2.5 py-1 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-50';
  return (
    <div className="mt-2" data-testid="draft-actions">
      <div className="flex items-center gap-2">
        <button type="button" onClick={showPreview} disabled={!ready || !!busy} className={btn} data-testid="draft-preview">
          {busy === 'preview' ? <Loader2 size={11} className="animate-spin" /> : <Eye size={11} />} Preview
        </button>
        <button type="button" onClick={sendTest} disabled={!ready || !!busy} className={btn} data-testid="draft-test-send">
          {busy === 'test' ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Send test to me
        </button>
        <span className="text-[10px] text-slate-500">{subjectNote} The test goes only to your own address.</span>
      </div>
      {preview && (
        <div className="mt-2 rounded-lg border border-slate-200 overflow-hidden" data-testid="draft-preview-panel">
          <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 flex items-center justify-between text-[11px]">
            <span className="text-slate-600">Subject: <strong data-testid="draft-preview-subject">{preview.subject}</strong></span>
            <button type="button" onClick={() => setPreview(null)} className="text-slate-500 hover:text-slate-800" data-testid="draft-preview-close">Close</button>
          </div>
          <iframe title="Email preview" sandbox="" srcDoc={preview.html} className="w-full h-64 bg-white" data-testid="draft-preview-frame" />
        </div>
      )}
    </div>
  );
}
